import {Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment, useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {BillData} from 'domain/usecases';
import {WhoIndicatesTagParametricEnum} from 'domain/usecases/private/home/who-indicates';
import {BillsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {CTAButtonFixed, MainFooter} from 'presentation/components';
import {AlertOperator} from 'presentation/components/AlertOperator';
import {LatePaymentAlert} from 'presentation/components/LatePaymentAlert';
import {Loading} from 'presentation/components/Loading/Loading';
import {WhoIndicatesCard} from 'presentation/components/WhoIndicatesCard';
import {FeaturesEnum, MyPaymentsEnum, OpenBillsTestIdEnum} from 'presentation/constants';
import {useContractStatus, useHostName} from 'presentation/hooks';
import {CustomCTAButtonFixedAction} from 'presentation/pages/private/authorizations/styles';
import {FinancialCard} from 'presentation/pages/private/home/components/FinancialCard';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {OperatorAlertEnum, OperatorNameEnum} from 'presentation/components/AlertOperator/types';
import {useSelectedBilletStore} from 'presentation/pages/private/my-payments-flow/store/selectedBilletStore';

import {OlderBillsModal} from '../components/OlderBillsModal';
import {useOlderBillsCheck} from '../hooks/useOlderBillsCheck';
import {ErrorBills} from '../../components/ErrorBills';
import {Container} from '../../styles';
import {CardEmpty} from '../components/CardEmpty';
import FinancialEmptyBigSvg from '../components/CardEmpty/svgr/FinancialEmptyBigSvg';
import {SubscriptionAlert} from './components/SubscriptionAlert';
import {useOpenBills} from './hooks/useOpenBills';
import {Grid, StyledContainer} from './styles';

const {
	emptyCardBillsTitle,
	emptyCardBillsTitleNotredameSaude,
	emptyCardBillsDescription,
	emptyCardBillsDescriptionNotredameSaude,
	title,
	titleNotredameSaude,
	buttonViewBills,
	buttonViewBillsNotredameSaude,
} = translations['pt-br'].privateFlow.myFinancial.openBills;

const {referAndEarn, loading} = translations['pt-br'].commons;

function OldOpenBills() {
	const {colors} = useTheme();
	const {isLaptop, isMobile} = useMediaQueries();
	const {isHolder} = useAccountInformation();
	const {isNotredameSaude} = useHostName();
	const {isValid} = useContractStatus();
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const {mergedBills, handleNavigation, isEmpty, showDelayAlert, isLoading, isError, refetch} =
		useOpenBills();

	const saoLucasOnlyBills = useMemo(
		() => mergedBills.filter(m => !!m.downloadLink).map(m => m.bill),
		[mergedBills],
	);

	const {
		checkOlderBills,
		numberOfBills,
		mostOldBill,
		currentBill,
		isModalOpen,
		handleCloseModal,
		actionType,
	} = useOlderBillsCheck(saoLucasOnlyBills);
	const {isEnabled: bioHealthFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ALERTA_BIOSAUDE,
	);
	const {isEnabled: saoLucasDownloadFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_DOWNLOAD_BILLS_SAO_LUCAS,
	);
	const setSelectedCurrentBillet = useSelectedBilletStore(state => state.setSelectedBillet);

	const {accesses} = useAccountDataStore(store => store.accountData);
	const {flBioSaude, flSaoLucasSaude} = accesses;

	const isBioHealthUser = bioHealthFlagIsEnabled && flBioSaude;
	const shouldShowSaoLucasAlert = !saoLucasDownloadFlagIsEnabled && flSaoLucasSaude;

	const handleGoToBillDetails = (bill: BillData) => {
		handleCloseModal();
		const mergedBill = mergedBills.find(m => m.bill.cdObrigacao === bill.cdObrigacao);
		const downloadLink = mergedBill?.downloadLink;
		setSelectedCurrentBillet(mergedBill?.bill ?? bill);
		if (downloadLink) {
			navigate(MyPaymentsEnum.BILLET_DETAILS, {
				state: {from: pathname, isSaoLucasBill: true, downloadLink},
			});
			return;
		}
		navigate(MyPaymentsEnum.BILLET_DETAILS, {state: pathname});
	};

	if (isLoading) {
		return (
			<StyledContainer>
				<Loading text={loading} />
			</StyledContainer>
		);
	}

	if (isError) {
		return (
			<StyledContainer>
				<ErrorBills handleTryAgain={refetch} />
			</StyledContainer>
		);
	}

	return (
		<Fragment>
			{isHolder ? (
				<SubscriptionAlert dataTagId={BillsTaggingEnum.CLICK_ADESAO_BOLETO} />
			) : null}
			<OlderBillsModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				numberOfBills={numberOfBills}
				mostOldBill={mostOldBill}
				currentBill={currentBill}
				onGoToBillDetails={handleGoToBillDetails}
				actionType={actionType === false ? undefined : actionType}
			/>
			<Container>
				<Stack gap={2}>
					{shouldShowSaoLucasAlert ? (
						<AlertOperator
							variant={OperatorAlertEnum.SAO_LUCAS_BILLS}
							operatorName={OperatorNameEnum.SAO_LUCAS_SAUDE}
						/>
					) : null}
					<Stack
						flexDirection={'row'}
						justifyContent={'space-between'}
						alignItems={'center'}>
						<Typography fontWeight={600} color={colors.light.text.secondary}>
							{isNotredameSaude ? titleNotredameSaude : title}
						</Typography>
						<CTAButtonFixed.Root
							direction={isLaptop ? 'column' : 'row'}
							justifyContent={'end'}>
							<CustomCTAButtonFixedAction
								data-tag-id={BillsTaggingEnum.CLICK_VISUALIZAR_BOLETOS_PAGOS}
								data-testid={OpenBillsTestIdEnum.BTN_VISUALIZAR_BOLETOS_PAGOS}
								fullWidth={isLaptop}
								onClick={() => handleNavigation(MyPaymentsEnum.PAYMENTS_HISTORY)}
								size="medium"
								variant="outlined">
								{isNotredameSaude ? buttonViewBillsNotredameSaude : buttonViewBills}
							</CustomCTAButtonFixedAction>
						</CTAButtonFixed.Root>
					</Stack>
					{isEmpty ? (
						<Stack gap={2}>
							{isBioHealthUser ? (
								<AlertOperator variant={OperatorAlertEnum.BILLS} />
							) : null}

							<CardEmpty
								title={
									isNotredameSaude
										? emptyCardBillsTitleNotredameSaude
										: emptyCardBillsTitle
								}
								illustration={<FinancialEmptyBigSvg />}>
								{isNotredameSaude
									? emptyCardBillsDescriptionNotredameSaude
									: emptyCardBillsDescription}
							</CardEmpty>
						</Stack>
					) : (
						<Stack gap={2}>
							{showDelayAlert ? (
								<LatePaymentAlert
									dataTagId={{
										openDrawerButton:
											BillsTaggingEnum.CLICK_SAIBA_MAIS_ALERTA_ATRASO_BOLETO,
										closeDrawerButton:
											BillsTaggingEnum.CLICK_FECHAR_DRAWER_ALERTA_ATRASO_BOLETO,
										understandButton:
											BillsTaggingEnum.CLICK_ENTENDI_DRAWER_ALERTA_ATRASO_BOLETO,
									}}
								/>
							) : null}

							{isBioHealthUser ? (
								<AlertOperator variant={OperatorAlertEnum.BILLS} />
							) : null}
							<Grid>
								{mergedBills.map(({bill, downloadLink}) => {
									const isSaoLucasBill = !!downloadLink;
									return (
										<FinancialCard
											key={bill.cdObrigacao}
											bill={bill}
											copyDataTagId={
												isSaoLucasBill
													? BillsTaggingEnum.CLICK_COPIAR_CODIGO_BOLETO_SAO_LUCAS
													: BillsTaggingEnum.CLICK_COPIAR_CODIGO_BOLETO_PAGINA_INICIAL
											}
											callDataTagId={
												isSaoLucasBill
													? undefined
													: BillsTaggingEnum.CLICK_FALAR_COM_ATENDIMENTO_BOLETO_PAGINA_INICIAL
											}
											detailsDataTagId={
												isSaoLucasBill
													? BillsTaggingEnum.CLICK_VER_DETALHES_BOLETO_SAO_LUCAS
													: undefined
											}
											copyTestId={
												isSaoLucasBill
													? OpenBillsTestIdEnum.BTN_COPIAR_CODIGO_BOLETO_SAO_LUCAS
													: OpenBillsTestIdEnum.BTN_COPIAR_CODIGO_BOLETO
											}
											detailsTestId={
												isSaoLucasBill
													? OpenBillsTestIdEnum.BTN_VER_DETALHES_BOLETO_SAO_LUCAS
													: OpenBillsTestIdEnum.BTN_VER_DETALHES_BOLETO
											}
											onCheckOlderBillsOnDetails={() => {
												const hasOlderBills = checkOlderBills(
													bill,
													'details',
												);
												if (hasOlderBills) return true;
												if (isSaoLucasBill) {
													setSelectedCurrentBillet(bill);
													navigate(MyPaymentsEnum.BILLET_DETAILS, {
														state: {
															from: pathname,
															isSaoLucasBill: true,
															downloadLink,
														},
													});
													return true;
												}

												return false;
											}}
											onCheckOlderBillsOnCopy={() =>
												checkOlderBills(bill, 'copy')
											}
										/>
									);
								})}
							</Grid>
						</Stack>
					)}
				</Stack>

				{isValid ? (
					<Stack gap={2} mt={isMobile ? 2 : 3}>
						<Typography fontWeight={600} color={colors.light.text.secondary}>
							{referAndEarn}
						</Typography>
						<WhoIndicatesCard
							$showAsBanner={!isMobile}
							dataTagId={BillsTaggingEnum.CLICK_SAIBA_MAIS_QUEM_INDICA_BOLETO}
							pathParametric={WhoIndicatesTagParametricEnum.BILL}
						/>
					</Stack>
				) : null}
			</Container>
			<MainFooter />
		</Fragment>
	);
}

export {OldOpenBills};
